/**
 * SiteScribe PWA — App.tsx (Day 5.3)
 *
 * Submission protocol now matches main.py exactly:
 *   POST /api/diary/submit
 *   Flat form fields: date, project_name, supervisor_name, recipients, answers_meta
 *   Audio:  multipart files, field name = audio_<qid>
 *   Photos: multipart files, field name = photo_<qid>_<idx>
 *
 * Plus all Day 5.2 features:
 *   • localStorage persistence for project + supervisor + recipients
 *   • In-app install guide (platform-aware)
 *   • European date format ("10 May 2026") in display
 */
import { useState, useRef, useEffect } from 'react';
import './styles.css';
import {
  enqueueDiary,
  listQueue,
  removeFromQueue,
  getQueueCount,
  buildFormData,
  generateSessionId,
  type QueuedDiary,
  type QueuedFile,
} from './queue';

// ============================================================================
// Question definitions
// ============================================================================
const QUESTIONS = [
  { id: 'q1', title: 'Weather & site conditions',        prompt: "Today's weather and overall site conditions — temperature, sky, wind, anything affecting work.",      skippable: false },
  { id: 'q2', title: 'Manpower on site',                  prompt: 'Walk through which contractors are here, what trades, rough headcounts.', skippable: true  },
  { id: 'q3', title: 'Equipment on site',                 prompt: 'Cranes, excavators, scaffolding, key tools — what is operational?',      skippable: true  },
  { id: 'q4', title: 'Activities completed',              prompt: 'What got done today, by area or WBS code.',                              skippable: true  },
  { id: 'q5', title: 'Deliveries, tests, inspections',    prompt: 'What arrived, what was tested, what was inspected, and the outcomes.',  skippable: true  },
  { id: 'q6', title: 'HSE events & quality issues',       prompt: 'Near-misses, incidents, NCRs raised, corrective actions taken.',        skippable: true  },
  { id: 'q7', title: 'Communications & instructions',     prompt: 'RFIs issued, instructions received, important conversations.',          skippable: true  },
  { id: 'q8', title: 'Concerns & risks',                  prompt: 'Anything that worries you about schedule, cost, claim exposure, or safety.', skippable: true },
  { id: 'q9', title: 'Look-ahead for tomorrow',           prompt: 'Planned activities for the next working day.',                          skippable: true  },
] as const;

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 90;
const MAX_RECORDING_SECONDS = 90;
const MAX_PHOTO_LONG_EDGE_PX = 1920;
const PHOTO_JPEG_QUALITY = 0.85;

const STORAGE_KEY_SUPERVISOR = 'sitescribe.v1.supervisor';
const STORAGE_KEY_PROJECT    = 'sitescribe.v1.project';
const STORAGE_KEY_RECIPIENTS = 'sitescribe.v1.recipients';

const QUEUE_SYNC_TAG = 'diary-submit-sync';

// API key — baked into the bundle at build time via Vite. Empty string means
// the backend is in dev/open mode (no auth required).
const API_KEY: string = ((import.meta as any).env?.VITE_API_KEY || '').toString();

// ============================================================================
// Types
// ============================================================================
type Screen = 'start' | 'help' | 'question' | 'review' | 'submitting' | 'queued' | 'done' | 'error';
type Platform = 'ios' | 'android' | 'desktop';

interface Photo { name: string; dataUrl: string; }

interface Answer {
  questionId: string;
  skipped: boolean;
  audioBlob: Blob | null;
  audioMime: string;
  audioDurationSec: number;
  photos: Photo[];
}

interface Meta {
  project: string;
  supervisor: string;
  date: string;       // ISO yyyy-mm-dd
  recipients: string; // raw multi-line/comma-separated input
}

// ============================================================================
// Helpers
// ============================================================================
const todayISO = () => new Date().toISOString().slice(0, 10);

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const formatEuropeanDate = (iso: string): string => {
  if (!iso) return '';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  const year = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const day = parseInt(m[3], 10);
  if (!year || !month || !day || month < 1 || month > 12) return iso;
  return `${day} ${MONTH_NAMES[month - 1]} ${year}`;
};

const parseRecipients = (raw: string): string[] => {
  if (!raw) return [];
  const parts = raw.replace(/\n/g, ',').split(',');
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of parts) {
    const e = p.trim().toLowerCase();
    if (!e) continue;
    if (!e.includes('@') || !e.includes('.')) continue;
    if (seen.has(e)) continue;
    seen.add(e);
    out.push(e);
  }
  return out;
};

const detectPlatform = (): Platform => {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
};

const pickAudioMime = (): string => {
  const candidates = ['audio/webm;codecs=opus','audio/webm','audio/mp4','audio/mp4;codecs=mp4a.40.2'];
  for (const m of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) return m;
  }
  return 'audio/webm';
};

const audioExtFromMime = (mime: string): string => {
  if (mime.includes('mp4')) return 'm4a';
  if (mime.includes('webm')) return 'webm';
  if (mime.includes('ogg')) return 'ogg';
  return 'audio';
};

const emptyAnswer = (qId: string): Answer => ({
  questionId: qId, skipped: false, audioBlob: null, audioMime: '', audioDurationSec: 0, photos: [],
});

const fileToCompressedDataUrl = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('FileReader error'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Image load error'));
      img.onload = () => {
        const MAX = MAX_PHOTO_LONG_EDGE_PX;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          if (width >= height) { height = Math.round((height * MAX) / width); width = MAX; }
          else { width = Math.round((width * MAX) / height); height = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Canvas 2D not supported'));
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', PHOTO_JPEG_QUALITY));
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
};

const dataUrlToBlob = async (dataUrl: string): Promise<Blob> => {
  const r = await fetch(dataUrl);
  return await r.blob();
};

const safeLoad = (key: string): string => {
  try { return localStorage.getItem(key) || ''; } catch { return ''; }
};
const safeSave = (key: string, value: string) => {
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
};
const safeClear = (key: string) => {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
};

// ============================================================================
// Main component
// ============================================================================
export default function App() {
  const [screen, setScreen] = useState<Screen>('start');
  const [questionIdx, setQuestionIdx] = useState(0);

  const [meta, setMeta] = useState<Meta>({
    project: '', supervisor: '', date: todayISO(), recipients: '',
  });
  const [answers, setAnswers] = useState<Record<string, Answer>>({});

  const [diaryId, setDiaryId] = useState<string | null>(null);
  const [statusText, setStatusText] = useState('Submitting…');
  const [errorText, setErrorText] = useState('');
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);

  const [platform] = useState<Platform>(detectPlatform());

  const [queueCount, setQueueCount] = useState(0);
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const durationTimerRef = useRef<number | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const pollTimerRef = useRef<number | null>(null);
  const pollAttemptsRef = useRef(0);

  // -------------------------- localStorage prefill on mount --------------------
  useEffect(() => {
    const supervisor = safeLoad(STORAGE_KEY_SUPERVISOR);
    const project    = safeLoad(STORAGE_KEY_PROJECT);
    const recipients = safeLoad(STORAGE_KEY_RECIPIENTS);
    if (supervisor || project || recipients) {
      setMeta(m => ({
        ...m,
        supervisor: supervisor || m.supervisor,
        project: project || m.project,
        recipients: recipients || m.recipients,
      }));
    }
  }, []);

  // -------------------------- queue lifecycle on mount -------------------------
  useEffect(() => {
    refreshQueueCount();
    // Attempt a flush on app open in case items are pending from a previous session
    flushQueueAttempt();

    const onOnline = () => {
      setIsOnline(true);
      // Try the in-page flush
      flushQueueAttempt();
      // Also nudge the SW to try (covers cases where the page is foregrounded
      // but the SW hadn't received the online signal yet)
      navigator.serviceWorker?.controller?.postMessage({ type: 'flush-queue' });
    };
    const onOffline = () => setIsOnline(false);

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    // Listen for SW messages about queue changes
    const swListener = (event: MessageEvent) => {
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'queue-item-sent' || data.type === 'queue-item-failed') {
        refreshQueueCount();
      }
    };
    navigator.serviceWorker?.addEventListener('message', swListener);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      navigator.serviceWorker?.removeEventListener('message', swListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      clearPollTimer();
      stopRecordingTimers();
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') recorder.stop();
      mediaStreamRef.current?.getTracks().forEach(t => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==========================================================================
  // Recording
  // ==========================================================================
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const mime = pickAudioMime();
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        const duration = (Date.now() - startedAtRef.current) / 1000;
        const q = QUESTIONS[questionIdx];
        setAnswers(prev => ({
          ...prev,
          [q.id]: { ...(prev[q.id] || emptyAnswer(q.id)), audioBlob: blob, audioMime: mime, audioDurationSec: duration, skipped: false },
        }));
        mediaStreamRef.current?.getTracks().forEach(t => t.stop());
        mediaStreamRef.current = null;
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      startedAtRef.current = Date.now();
      setRecordingDuration(0);
      setIsRecording(true);
      durationTimerRef.current = window.setInterval(() => {
        const sec = Math.floor((Date.now() - startedAtRef.current) / 1000);
        setRecordingDuration(sec);
        if (sec >= MAX_RECORDING_SECONDS) stopRecording();
      }, 250);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrorText(`Microphone error: ${msg}. Make sure you allowed mic access.`);
      setScreen('error');
    }
  };

  const stopRecording = () => {
    stopRecordingTimers();
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    setIsRecording(false);
  };

  const stopRecordingTimers = () => {
    if (durationTimerRef.current !== null) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
  };

  const clearAudio = (qId: string) => {
    setAnswers(prev => {
      const ans = prev[qId];
      if (!ans) return prev;
      return { ...prev, [qId]: { ...ans, audioBlob: null, audioDurationSec: 0, audioMime: '' } };
    });
  };

  // ==========================================================================
  // Photos
  // ==========================================================================
  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const q = QUESTIONS[questionIdx];
    const newPhotos: Photo[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const dataUrl = await fileToCompressedDataUrl(file);
        newPhotos.push({ name: file.name, dataUrl });
      } catch (err) { console.error('Photo processing failed for', file.name, err); }
    }
    setAnswers(prev => {
      const existing = prev[q.id] || emptyAnswer(q.id);
      return { ...prev, [q.id]: { ...existing, photos: [...existing.photos, ...newPhotos], skipped: false } };
    });
    e.target.value = '';
  };

  const removePhoto = (qId: string, idx: number) => {
    setAnswers(prev => {
      const ans = prev[qId];
      if (!ans) return prev;
      return { ...prev, [qId]: { ...ans, photos: ans.photos.filter((_, i) => i !== idx) } };
    });
  };

  // ==========================================================================
  // Navigation
  // ==========================================================================
  const beginDiary = () => {
    if (!meta.project.trim() || !meta.supervisor.trim()) {
      alert('Project and supervisor are required.');
      return;
    }
    const validRecipients = parseRecipients(meta.recipients);
    if (validRecipients.length === 0) {
      alert('Please enter at least one valid recipient email.');
      return;
    }
    safeSave(STORAGE_KEY_SUPERVISOR, meta.supervisor.trim());
    safeSave(STORAGE_KEY_PROJECT,    meta.project.trim());
    safeSave(STORAGE_KEY_RECIPIENTS, meta.recipients.trim());
    setScreen('question');
    setQuestionIdx(0);
  };

  const skipQuestion = () => {
    const q = QUESTIONS[questionIdx];
    if (!q.skippable) return;
    if (isRecording) stopRecording();
    setAnswers(prev => ({
      ...prev,
      [q.id]: { ...(prev[q.id] || emptyAnswer(q.id)), skipped: true },
    }));
    advance();
  };

  const advance = () => {
    if (isRecording) stopRecording();
    if (questionIdx < QUESTIONS.length - 1) setQuestionIdx(questionIdx + 1);
    else setScreen('review');
  };

  const goBack = () => {
    if (isRecording) stopRecording();
    if (questionIdx > 0) setQuestionIdx(questionIdx - 1);
    else setScreen('start');
  };

  const clearSavedData = () => {
    safeClear(STORAGE_KEY_SUPERVISOR);
    safeClear(STORAGE_KEY_PROJECT);
    safeClear(STORAGE_KEY_RECIPIENTS);
    setMeta({ project: '', supervisor: '', date: todayISO(), recipients: '' });
  };

  // ==========================================================================
  // Submission + polling — protocol matches main.py exactly
  // ==========================================================================

  /** Build a fully serializable QueuedDiary payload from current form state. */
  const buildPayload = async (): Promise<QueuedDiary> => {
    const sessionId = generateSessionId();
    const recipientsParsed = parseRecipients(meta.recipients);

    const answersMeta: Record<string, any> = {};
    for (const q of QUESTIONS) {
      const a = answers[q.id];
      const audioOk = !!a?.audioBlob;
      const photoCount = a?.photos.length || 0;
      const ext = audioOk ? audioExtFromMime(a!.audioMime) : null;
      answersMeta[q.id] = {
        title: q.title,
        skipped: !a || a.skipped || (!audioOk && photoCount === 0),
        audio_filename: audioOk ? `audio_${q.id}.${ext}` : null,
        audio_duration_sec: a?.audioDurationSec || 0,
        photo_filenames: (a?.photos || []).map((_, i) => `photo_${q.id}_${i}.jpg`),
      };
    }

    const fields: Record<string, string> = {
      date: meta.date,
      project_name: meta.project,
      supervisor_name: meta.supervisor,
      recipients: JSON.stringify(recipientsParsed),
      answers_meta: JSON.stringify(answersMeta),
    };
    if (API_KEY) {
      fields.api_key = API_KEY;
    }

    const files: QueuedFile[] = [];
    for (const q of QUESTIONS) {
      const a = answers[q.id];
      if (a?.audioBlob) {
        const ext = audioExtFromMime(a.audioMime);
        files.push({ field: `audio_${q.id}`, blob: a.audioBlob, filename: `audio_${q.id}.${ext}` });
      }
      if (a?.photos.length) {
        for (let i = 0; i < a.photos.length; i++) {
          try {
            const blob = await dataUrlToBlob(a.photos[i].dataUrl);
            files.push({ field: `photo_${q.id}_${i}`, blob, filename: `photo_${q.id}_${i}.jpg` });
          } catch (err) {
            console.warn('Failed to attach photo', q.id, i, err);
          }
        }
      }
    }

    return {
      id: sessionId,
      createdAt: Date.now(),
      attempts: 0,
      lastError: null,
      lastAttemptAt: null,
      state: 'queued',
      fields,
      files,
      summary: { project: meta.project, date: meta.date },
    };
  };

  const refreshQueueCount = async () => {
    try {
      const n = await getQueueCount();
      setQueueCount(n);
    } catch {
      /* ignore */
    }
  };

  const registerBackgroundSync = async (): Promise<void> => {
    try {
      if ('serviceWorker' in navigator && 'SyncManager' in window) {
        const reg = await navigator.serviceWorker.ready;
        await (reg as any).sync.register(QUEUE_SYNC_TAG);
      }
    } catch (err) {
      console.warn('Background Sync registration failed (will fall back to online events):', err);
    }
  };

  /** Try to drain the queue from the page. Used on mount, on online events,
   *  and via the "Retry now" button. Stops on the first network error
   *  to avoid hammering a flaky connection. */
  const flushQueueAttempt = async (): Promise<void> => {
    let items: QueuedDiary[];
    try {
      items = await listQueue();
    } catch {
      return;
    }
    if (items.length === 0) return;

    for (const item of items) {
      try {
        const fd = buildFormData(item);
        const resp = await fetch('/api/diary/submit', { method: 'POST', body: fd });
        if (resp.ok) {
          await removeFromQueue(item.id);
        } else {
          // Server-side rejection (e.g. 4xx). Don't drop — server might recover.
          // But stop here so we don't burn through all items in one bad state.
          break;
        }
      } catch {
        // Network blip. Leave in queue.
        break;
      }
    }
    await refreshQueueCount();
  };

  const submitDiary = async () => {
    setScreen('submitting');
    setStatusText('Uploading…');

    let item: QueuedDiary;
    try {
      item = await buildPayload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrorText(`Failed to prepare submission: ${msg}`);
      setScreen('error');
      return;
    }

    try {
      const fd = buildFormData(item);
      const resp = await fetch('/api/diary/submit', { method: 'POST', body: fd });
      if (!resp.ok) {
        const body = await resp.text();
        throw new Error(`Server returned ${resp.status}: ${body.slice(0, 200)}`);
      }
      const data = await resp.json();
      const id = data.session_id || data.id || data.diary_id || item.id;
      setDiaryId(id);
      pollAttemptsRef.current = 0;
      pollTimerRef.current = window.setInterval(() => pollStatus(id), POLL_INTERVAL_MS);
      pollStatus(id);
    } catch (err) {
      // Distinguish network-class errors (offline, DNS, TLS, request aborted)
      // from server-class errors. TypeError is what fetch throws for network
      // failures; navigator.onLine reflects current connectivity.
      const isNetworkError =
        err instanceof TypeError || (typeof navigator !== 'undefined' && !navigator.onLine);

      if (isNetworkError) {
        try {
          await enqueueDiary(item);
          await registerBackgroundSync();
          await refreshQueueCount();
          setScreen('queued');
        } catch (qerr) {
          const msg = qerr instanceof Error ? qerr.message : String(qerr);
          setErrorText(`Could not save offline: ${msg}`);
          setScreen('error');
        }
      } else {
        const msg = err instanceof Error ? err.message : String(err);
        setErrorText(msg);
        setScreen('error');
      }
    }
  };

  const pollStatus = async (id: string) => {
    pollAttemptsRef.current += 1;
    if (pollAttemptsRef.current > POLL_MAX_ATTEMPTS) {
      clearPollTimer();
      setErrorText('Processing is taking longer than expected. Check your email or contact support.');
      setScreen('error');
      return;
    }
    try {
      const resp = await fetch(`/api/diary/${id}/status`);
      if (!resp.ok) throw new Error(`Status check failed: ${resp.status}`);
      const data = await resp.json();
      const state = String(data.state || data.status || '').toLowerCase();
      const stage = data.stage || data.step || '';
      const labelMap: Record<string, string> = {
        queued: 'Queued for processing…',
        running: stage ? `Working: ${stage}` : 'Processing…',
        transcribing: 'Transcribing your voice…',
        structuring: 'Structuring the diary…',
        rendering: 'Rendering the PDF…',
        emailing: 'Sending the email…',
        done: 'Diary ready',
        error: data.error || 'Unknown error',
      };
      setStatusText(labelMap[state] || `Working: ${state}`);
      if (state === 'done') {
        clearPollTimer();
        setPdfUrl(`/api/diary/${id}/pdf`);
        setScreen('done');
      } else if (state === 'error' || state === 'failed') {
        clearPollTimer();
        setErrorText(data.error || data.message || 'Processing failed.');
        setScreen('error');
      }
    } catch (err) {
      console.warn('Poll error (will retry):', err);
    }
  };

  const clearPollTimer = () => {
    if (pollTimerRef.current !== null) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const resetForNewDiary = () => {
    clearPollTimer();
    setScreen('start');
    setQuestionIdx(0);
    setAnswers({});
    setDiaryId(null);
    setStatusText('Submitting…');
    setErrorText('');
    setPdfUrl(null);
  };

  // ==========================================================================
  // Render dispatch
  // ==========================================================================
  if (screen === 'start')      return renderStart();
  if (screen === 'help')       return renderHelp();
  if (screen === 'question')   return renderQuestion();
  if (screen === 'review')     return renderReview();
  if (screen === 'submitting') return renderSubmitting();
  if (screen === 'queued')     return renderQueued();
  if (screen === 'done')       return renderDone();
  if (screen === 'error')      return renderError();
  return null;

  // --------------------------------------------------------------------------
  function renderStart() {
    const hasSavedData = !!(safeLoad(STORAGE_KEY_SUPERVISOR) || safeLoad(STORAGE_KEY_PROJECT) || safeLoad(STORAGE_KEY_RECIPIENTS));
    return (
      <div className="screen screen-start">
        <header className="ss-header">
          <div className="brand">
            <div className="brand-icon" aria-hidden="true" />
            <div className="brand-name">SiteScribe</div>
          </div>
          <button className="link-help" onClick={() => setScreen('help')}>?</button>
        </header>
        <main className="ss-main">
          <div className="eyebrow">Daily site diary</div>
          <h1>Walk the site. Talk for 5 minutes. Get the PDF.</h1>
          <p className="lede">Nine quick questions. Your answers become a structured FIDIC-style report by email.</p>

          {queueCount > 0 && (
            <div className="queue-banner" role="status">
              <div className="queue-banner-text">
                <strong>{queueCount}</strong> diary{queueCount > 1 ? ' submissions' : ' submission'} waiting to send.
                {isOnline
                  ? ' We will send automatically — or retry now.'
                  : ' We will send when you are back online.'}
              </div>
              {isOnline && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => flushQueueAttempt()}
                  type="button"
                >
                  Retry now
                </button>
              )}
            </div>
          )}

          <div className="form-stack">
            <label className="field">
              <span className="field-label">Project</span>
              <input type="text" value={meta.project}
                onChange={e => setMeta({ ...meta, project: e.target.value })}
                placeholder="e.g., Bahnhofstrasse PV Lot 2"
                autoComplete="off" autoCapitalize="words" />
            </label>
            <label className="field">
              <span className="field-label">Supervisor</span>
              <input type="text" value={meta.supervisor}
                onChange={e => setMeta({ ...meta, supervisor: e.target.value })}
                placeholder="Your name" autoComplete="name" />
            </label>
            <label className="field">
              <span className="field-label">Date</span>
              <input type="date" value={meta.date}
                onChange={e => setMeta({ ...meta, date: e.target.value })} />
            </label>
            <label className="field">
              <span className="field-label">Recipients</span>
              <textarea value={meta.recipients}
                onChange={e => setMeta({ ...meta, recipients: e.target.value })}
                placeholder="director@firm.com, doc-control@firm.com"
                rows={3}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false} />
              <span className="field-hint">
                Separate multiple emails with commas. The PDF is sent to all addresses on this list.
              </span>
            </label>
          </div>

          <button className="btn btn-primary btn-lg" onClick={beginDiary}>
            Begin diary →
          </button>

          <div className="start-secondary">
            <button className="btn-text" onClick={() => setScreen('help')}>
              First time? See the install guide
            </button>
            {hasSavedData && (
              <button className="btn-text muted" onClick={() => {
                if (confirm('Clear your saved name, project, and recipients from this device?')) clearSavedData();
              }}>
                Clear saved data (sharing this device)
              </button>
            )}
          </div>
        </main>
      </div>
    );
  }

  // --------------------------------------------------------------------------
  function renderHelp() {
    return (
      <div className="screen screen-help">
        <header className="ss-header">
          <button className="btn-icon" onClick={() => setScreen('start')} aria-label="Back">←</button>
          <div className="progress">
            <span className="progress-num">Install guide</span>
          </div>
        </header>
        <main className="ss-main">
          <div className="eyebrow">First time?</div>
          <h2>Install SiteScribe on this device</h2>
          <p className="lede">
            Installing means SiteScribe gets its own icon on your home screen, opens fullscreen, and remembers your project and name. Takes 30 seconds.
          </p>

          {platform === 'ios' && <HelpStepsIOS />}
          {platform === 'android' && <HelpStepsAndroid />}
          {platform === 'desktop' && <HelpStepsDesktop />}

          <div className="help-section">
            <h3>What to do the first time you open it</h3>
            <ol className="help-checklist">
              <li>Tap <strong>Begin diary</strong>. Your name, project, and recipients are saved automatically; you only enter them once.</li>
              <li>When the app asks for <strong>microphone access</strong>, tap Allow. Without it, voice recording will not work.</li>
              <li>When you first tap the <strong>Camera</strong> button, allow camera access. When you first tap <strong>Library</strong>, allow photo access.</li>
              <li>Try a short test: record 10 seconds, attach one photo, skip the rest, submit. Confirm the PDF email arrives at the recipient list.</li>
            </ol>
          </div>

          <div className="help-section">
            <h3>Troubleshooting</h3>
            <ul className="help-list">
              <li><strong>Microphone permission denied?</strong> On iOS: Settings → SiteScribe → Microphone → On. On Android: long-press the icon → App info → Permissions → Microphone → Allow.</li>
              <li><strong>The app feels stuck?</strong> Force-quit (swipe up on iOS / recent apps on Android), reopen. Your draft is not saved yet at that point, but your name, project, and recipients are.</li>
              <li><strong>Email did not arrive?</strong> Check the spam folder of every recipient. Within 60 seconds, the PDF should be there. If it is not after 5 minutes, contact <a href="mailto:jacopo@anvilship.eu">jacopo@anvilship.eu</a>.</li>
            </ul>
          </div>

          <button className="btn btn-primary btn-lg" onClick={() => setScreen('start')}>
            Got it →
          </button>
        </main>
      </div>
    );
  }

  function renderQuestion() {
    const q = QUESTIONS[questionIdx];
    const a = answers[q.id];
    const hasAudio = !!a?.audioBlob;
    const hasPhotos = (a?.photos.length || 0) > 0;
    const audioStatusText = isRecording
      ? `Recording ${recordingDuration}s — tap to stop`
      : hasAudio
      ? `Recorded ${Math.round(a!.audioDurationSec)}s — tap mic to re-record`
      : 'Tap the mic to record';

    return (
      <div className="screen screen-question">
        <header className="ss-header">
          <button className="btn-icon" onClick={goBack} aria-label="Back">←</button>
          <div className="progress">
            <span className="progress-num">Question {questionIdx + 1} / {QUESTIONS.length}</span>
            <div className="progress-track">
              <div className="progress-fill" style={{ width: `${((questionIdx + 1) / QUESTIONS.length) * 100}%` }} />
            </div>
          </div>
        </header>
        <main className="ss-main">
          <div>
            <h2>{q.title}</h2>
            <p className="prompt">{q.prompt}</p>
          </div>
          <div className="record-zone">
            <button type="button"
              className={`record-btn ${isRecording ? 'recording' : hasAudio ? 'has-audio' : ''}`}
              onClick={isRecording ? stopRecording : startRecording}
              aria-label={isRecording ? 'Stop recording' : 'Start recording'}>
              <span className="record-icon" aria-hidden="true" />
            </button>
            <div className="record-status">{audioStatusText}</div>
            {hasAudio && !isRecording && (
              <button className="btn-text" onClick={() => clearAudio(q.id)}>Discard recording</button>
            )}
          </div>
          <div className="photo-zone">
            <div className="photo-buttons">
              <button type="button" className="photo-btn" onClick={() => cameraInputRef.current?.click()}>
                📷 Camera
              </button>
              <button type="button" className="photo-btn" onClick={() => galleryInputRef.current?.click()}>
                🖼️ Library
              </button>
            </div>
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
                   multiple style={{ display: 'none' }} onChange={handlePhotoChange} />
            <input ref={galleryInputRef} type="file" accept="image/*"
                   multiple style={{ display: 'none' }} onChange={handlePhotoChange} />
            {hasPhotos && (
              <div className="photo-thumbs">
                {a!.photos.map((p, idx) => (
                  <div key={idx} className="photo-thumb">
                    <img src={p.dataUrl} alt={`Photo ${idx + 1}`} />
                    <button className="photo-remove" onClick={() => removePhoto(q.id, idx)} aria-label="Remove photo">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
        <footer className="ss-footer">
          {q.skippable && <button className="btn btn-ghost" onClick={skipQuestion}>Skip</button>}
          <button className="btn btn-primary" onClick={advance}
                  disabled={!hasAudio && !hasPhotos && !q.skippable}>
            {questionIdx === QUESTIONS.length - 1 ? 'Review →' : 'Next →'}
          </button>
        </footer>
      </div>
    );
  }

  function renderReview() {
    const answeredCount = QUESTIONS.filter(q => {
      const a = answers[q.id];
      return a && !a.skipped && (a.audioBlob || a.photos.length > 0);
    }).length;
    const skippedCount = QUESTIONS.length - answeredCount;
    const recipientList = parseRecipients(meta.recipients);
    return (
      <div className="screen screen-review">
        <header className="ss-header">
          <button className="btn-icon"
                  onClick={() => { setScreen('question'); setQuestionIdx(QUESTIONS.length - 1); }}
                  aria-label="Back">←</button>
          <div className="progress"><span className="progress-num">Review</span></div>
        </header>
        <main className="ss-main">
          <div className="eyebrow">Almost done</div>
          <h2>Review and submit</h2>
          <p className="lede"><strong>{meta.project}</strong> · {meta.supervisor} · {formatEuropeanDate(meta.date)}</p>
          <p className="lede" style={{ fontSize: '14px' }}>
            Sending to: {recipientList.join(', ')}
          </p>
          <ul className="review-list">
            {QUESTIONS.map((q, idx) => {
              const a = answers[q.id];
              const audioOk = !!a?.audioBlob;
              const photoCount = a?.photos.length || 0;
              const skipped = !a || a.skipped || (!audioOk && photoCount === 0);
              return (
                <li key={q.id} className={`review-item ${skipped ? 'skipped' : ''}`}>
                  <span className="review-num">{idx + 1}</span>
                  <span className="review-title">{q.title}</span>
                  <span className="review-status">
                    {skipped ? 'Skipped'
                      : `${audioOk ? `${Math.round(a!.audioDurationSec)}s` : 'no audio'}${photoCount ? ` · ${photoCount} ${photoCount === 1 ? 'photo' : 'photos'}` : ''}`}
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="hint-line">{answeredCount} answered · {skippedCount} skipped</p>
          <button className="btn btn-primary btn-lg" onClick={submitDiary} disabled={answeredCount === 0}>
            Submit diary →
          </button>
        </main>
      </div>
    );
  }

  function renderSubmitting() {
    return (
      <div className="screen screen-submitting">
        <main className="ss-main center">
          <div className="spinner" />
          <h2>{statusText}</h2>
          <p className="lede">This usually takes 30 to 90 seconds.</p>
        </main>
      </div>
    );
  }

  function renderQueued() {
    return (
      <div className="screen screen-done">
        <main className="ss-main center">
          <div className="check-mark" style={{ fontSize: '64px' }}>⏳</div>
          <h2>Saved for later</h2>
          <p className="lede">
            {isOnline
              ? "The connection failed mid-upload. Your diary is safely stored on this device and will retry automatically."
              : "You're offline. Your diary is safely stored on this device. We'll send it as soon as you're back online — you can close the app."}
          </p>
          {queueCount > 0 && (
            <p className="lede" style={{ fontSize: '14px', opacity: 0.7 }}>
              {queueCount} item{queueCount > 1 ? 's' : ''} in the queue.
            </p>
          )}
          <div className="actions">
            {isOnline && (
              <button className="btn btn-primary" onClick={() => flushQueueAttempt()}>
                Try sending now
              </button>
            )}
            <button className="btn btn-ghost" onClick={resetForNewDiary}>
              Start another diary
            </button>
          </div>
        </main>
      </div>
    );
  }

  function renderDone() {
    return (
      <div className="screen screen-done">
        <main className="ss-main center">
          <div className="check-mark">✓</div>
          <h2>Diary ready</h2>
          <p className="lede">The PDF has been emailed to your recipient list.</p>
          <div className="actions">
            {pdfUrl && <a href={pdfUrl} className="btn btn-primary btn-lg" target="_blank" rel="noopener">Download PDF</a>}
            <button className="btn btn-ghost" onClick={resetForNewDiary}>Submit another</button>
          </div>
        </main>
      </div>
    );
  }

  function renderError() {
    return (
      <div className="screen screen-error">
        <main className="ss-main center">
          <div className="x-mark">✕</div>
          <h2>Something went wrong</h2>
          <p className="lede">{errorText || 'Unknown error.'}</p>
          <div className="actions">
            <button className="btn btn-primary" onClick={resetForNewDiary}>Start over</button>
          </div>
        </main>
      </div>
    );
  }
}

// ============================================================================
// Help screen — platform-specific install instructions
// ============================================================================
function HelpStepsIOS() {
  return (
    <div className="help-section">
      <h3>📱 On iPhone or iPad (Safari)</h3>
      <ol className="help-steps">
        <li><strong>Open this page in Safari</strong> (not Chrome — Safari is the only browser that supports installing on iOS).</li>
        <li>
          <strong>Tap the Share button</strong> at the bottom of the screen. It looks like a square with an arrow pointing up.
          <div className="hint-mockup ios-share-bar">
            <span>‹</span><span>›</span>
            <span className="hint-target" aria-label="Share icon">⤴</span>
            <span>📖</span><span>⊞</span>
          </div>
        </li>
        <li><strong>Scroll down in the share menu</strong> and tap <em>Add to Home Screen</em>.</li>
        <li>Confirm the name <strong>SiteScribe</strong> and tap <em>Add</em> in the top-right corner. The app icon now lives on your home screen.</li>
        <li>Always open SiteScribe from the home-screen icon, not from Safari. The home-screen version runs fullscreen and remembers your data.</li>
      </ol>
    </div>
  );
}

function HelpStepsAndroid() {
  return (
    <div className="help-section">
      <h3>🤖 On Android (Chrome)</h3>
      <ol className="help-steps">
        <li><strong>Open this page in Chrome</strong> (Firefox and Samsung Internet also work, but Chrome is recommended).</li>
        <li>
          Chrome usually shows an <em>Install app</em> banner at the bottom. Tap <strong>Install</strong>.
          <div className="hint-mockup android-banner">
            <span>SiteScribe</span>
            <span className="hint-target">Install</span>
          </div>
        </li>
        <li>If no banner appears, tap the <strong>three-dots menu</strong> (top-right of Chrome) and choose <em>Install app</em> or <em>Add to Home screen</em>.</li>
        <li>Confirm. The icon appears on your home screen and in the app drawer.</li>
        <li>Always open SiteScribe from the home-screen icon. The installed version runs fullscreen and keeps your data between sessions.</li>
      </ol>
    </div>
  );
}

function HelpStepsDesktop() {
  return (
    <div className="help-section">
      <h3>💻 On Mac, Windows, or Linux (Chrome or Edge)</h3>
      <ol className="help-steps">
        <li>Open this page in <strong>Chrome</strong> or <strong>Microsoft Edge</strong>.</li>
        <li>
          Look at the right side of the address bar. You'll see a small <strong>install icon</strong> (a monitor with a downward arrow, or a plus inside a square).
          <div className="hint-mockup desktop-addressbar">
            <span className="dab-url">sitescribe.anvilship.eu</span>
            <span className="hint-target" aria-label="Install icon">⊞</span>
          </div>
        </li>
        <li>Click that icon, then <strong>Install</strong>. SiteScribe opens as its own window and gets a shortcut on your desktop or Start menu.</li>
      </ol>
      <p className="lede" style={{ fontSize: '14px', marginTop: '12px' }}>
        Note: desktop microphones work fine for recording, but for site photos you'll have a better experience on phone or tablet.
      </p>
    </div>
  );
}
